<template>
  <dialog
    v-show="isOpen"
    :class="{ open: isOpen }"
    id="modal-dialog"
    data-cy="modal-dialog"
    open
  >
    <div :class="`modal-container ${containerClass || ''}`">
      <header :class="headerClass || ''" data-cy="modal-header">
        <slot name="header">
          <h3>{{ headerText }}</h3>
          <span class="modal__close" @click="closeModal"
            ><font-awesome-icon icon="times"
          /></span>
        </slot>
      </header>
      <div
        :class="`modal-content ${contentClass || ''}`"
        data-cy="modal-content"
      >
        <slot name="content"></slot>
      </div>
    </div>
    <div class="modal-bg" @click="closeModal"></div>
  </dialog>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watchEffect } from "vue";

export interface Props {
  open: boolean;
  containerClass?: string;
  contentClass?: string;
  headerClass?: string;
  headerText?: string;
  hideOverflow?: boolean;
  canBeClosed?: boolean;
  confirmOnClose?: boolean;
  closeConfirmationMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  contentClass: "",
  headerClass: "",
  headerText: "",
  hideOverflow: true,
  scrollToTop: true,
  canBeClosed: true,
  confirmOnClose: false,
  closeConfirmationMessage: "Are you sure you want to close this modal?",
});

const isOpen = ref<boolean>(props.open);

const emit = defineEmits<{
  (e: "closeModal", closed: boolean): void;
}>();

const closeModal = function () {
  const closed = props.canBeClosed == undefined || props.canBeClosed;
  if (!closed) {
    emit("closeModal", false);
    return;
  }

  if (props.confirmOnClose && !window.confirm(props.closeConfirmationMessage)) {
    emit("closeModal", false);
    return;
  }

  isOpen.value = false;
  modalClosed();
  emit("closeModal", true);
};

const modalOpened = function () {
  if (props.hideOverflow) document.body.style.overflow = "hidden";
};

const modalClosed = function () {
  if (props.hideOverflow) document.body.style.overflow = "auto";
};

watchEffect(() => {
  isOpen.value = props.open;
  if (props.open) modalOpened();
  else modalClosed();
});

onUnmounted(() => {
  modalClosed();
});
</script>

<style lang="scss">
dialog {
  width: 100%;
  height: 100%;
  top: 0;
  border: none;
  background: none;
  display: flex;
  justify-content: center;
  padding: 0;

  @include media-breakpoint-up(sm) {
    padding: 1em;
  }

  .modal-bg {
    position: fixed;
    z-index: 110;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: rgba(0, 0, 0, 0.4);
  }

  .modal-container {
    border: none;
    border-top: 7px solid #4a90e2;
    padding: 20px;
    display: block;
    max-height: 100%;
    background-color: white;
    box-shadow: 0 2px 8px rgbal(0, 0, 0, 0.26);
    z-index: 1000;
    width: 100%;
    position: fixed;
    height: 100%;

    @include media-breakpoint-up(sm) {
      width: unset;
      height: auto;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .modal__close {
      font-size: 18px;
      padding: 6px 10px;
      cursor: pointer;
      &:hover {
        color: #444;
      }
    }

    .modal-content {
      position: relative;
      overflow: auto;
      max-height: calc(100% - 24px);

      @include media-breakpoint-up(sm) {
        max-height: 80vh;
      }
    }

    .product-btn {
      margin-right: 10px;
      color: #fff;
      background-color: #337ab7;
      border-color: #2e6da4;
      border-bottom: 2px solid #235784;
    }
  }
}

.flex-container {
  display: flex;
  flex-wrap: nowrap;
  margin-top: 10px;
  margin-bottom: 20px;
}
.flex__left {
  flex: 1;
  margin-right: 40px;
}
.flex__right {
  flex: 3;
}
.grid-container {
  overflow: auto;
  max-height: 440px;
  border: 1px solid #ddd;
}
.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}
.form-row label {
  font-weight: 200;
  text-align: right;
  min-width: 125px;
  margin-right: 20px;
}

td {
  font-size: 13px;
}
</style>
